/* eslint-disable no-secrets/no-secrets */
/**
 * Shared form data builder utility.
 *
 * Transforms `UseCampaignFormResult` state into a `CampaignData` object
 * suitable for API submission. Reused by both Create and Modify flows.
 *
 * Common field mapping and date/ASIN parsing live here; campaign-type-specific
 * guardrail and attribute builders are in dedicated modules:
 *   - ncemiDataBuilder.ts
 *   - cashbackDataBuilder.ts
 *   - buybackDataBuilder.ts
 *   - dealDataBuilder.ts
 *
 * @module formDataBuilder
 */
/* eslint-enable no-secrets/no-secrets */

import { CampaignData } from 'src/@types/valueMatching/campaign';
import { CampaignConfigurationData } from 'src/@types/valueMatching/campaignConfiguration';
import { CampaignType } from 'src/@types/valueMatching/enums';
import { UseCampaignFormResult } from 'src/hooks/useCampaignForm/useCampaignForm';
import { localPartsToEpoch } from 'src/utils/commonUtils';

import { buildNcemiGuardrails, buildNcemiAttributes } from './builders/ncemiDataBuilder';
import { buildCashbackGuardrail, buildCashbackAttributes } from './builders/cashbackDataBuilder';
import { buildBuybackGuardrail, buildBuybackAttributes } from './builders/buybackDataBuilder';
import { buildDealGuardrails } from './builders/dealDataBuilder';
import { buildInstantBankDiscountGuardrails, buildInstantBankDiscountAttributes } from './builders/instantBankDiscountDataBuilder';


// ============================================================================
// Public API
// ============================================================================

/**
 * Builds a `CampaignData` object from form state, campaign type, and configuration.
 *
 * @param form - The form state from `useCampaignForm`
 * @param campaignType - The campaign type enum value
 * @param config - Server-provided campaign configuration (needed for brand OFA mapping)
 * @param existingData - Optional existing campaign data (for Modify flow — includes campaignId + version)
 * @returns A `CampaignData` object ready for API submission
 */
export function buildCampaignDataFromForm(
    form: UseCampaignFormResult,
    campaignType: CampaignType,
    config: CampaignConfigurationData | null,
    existingData?: CampaignData,
): CampaignData {
    const data: CampaignData = {};

    // ── Existing data (Modify flow) ─────────────────────────────────────
    if (existingData?.campaignId) {
        data.campaignId = existingData.campaignId;
    }
    if (existingData?.version != null) {
        data.version = existingData.version;
    }

    // ── Common fields ───────────────────────────────────────────────────
    data.campaignType = campaignType;
    data.title = form.title;
    data.cpgnAccount = form.selectedFundingAccount;

    // Merchant group — only for NCEMI and DEAL campaign types.
    // CASHBACK and BUYBACK campaigns are not merchant-scoped; they apply across all merchants.
    if (campaignType === CampaignType.NO_COST_EMI || campaignType === CampaignType.DEAL) {
        if (form.selectedMerchantGroup) {
            data.merchantDetail = {
                merchantGroup: form.selectedMerchantGroup,
                merchantIds: [],
            };
        }
    }

    // ── Date/time conversion ────────────────────────────────────────────
    // The form stores dates in IST (useCommonFormState converts epoch → IST on init).
    // toEpochMillis converts IST strings back to epoch (subtracts 5:30).
    // This roundtrip is lossless, so we always use toEpochMillis for both create and modify.
    data.startTime = toEpochMillis(form.startDate, form.startTime);
    data.endTime = toEpochMillis(form.endDate, form.endTime);

    // ── ASIN parsing ────────────────────────────────────────────────────
    data.includedProducts = { asins: parseAsins(form.asins) };

    // ── Suspended products (Modify flow, NCEMI/DEAL only) ───────────────
    // BUYBACK uses `excludedOldItemIDs` via `buildBuybackAttributes`; CASHBACK has no
    // suspended products. Only set the key when the parsed array is non-empty so we
    // don't emit an empty `suspendedProducts` in the payload.
    if (campaignType === CampaignType.NO_COST_EMI || campaignType === CampaignType.DEAL) {
        const suspendedAsins = parseAsins(form.suspendedAsins);
        if (suspendedAsins.length > 0) {
            data.suspendedProducts = { asins: suspendedAsins };
        }
    }

    // ── Campaign-type-specific guardrails and attributes ────────────────
    switch (campaignType) {
        case CampaignType.NO_COST_EMI:
            data.guardrails = { noCostEMI: buildNcemiGuardrails(form) };
            data.attributes = { noCostEMI: buildNcemiAttributes(form, config) };
            break;
        case CampaignType.CASHBACK:
            data.guardrails = { cashback: buildCashbackGuardrail(form) };
            data.attributes = { cashback: buildCashbackAttributes(form) };
            break;
        case CampaignType.BUYBACK:
            data.guardrails = { buyback: buildBuybackGuardrail(form) };
            data.attributes = { buyback: buildBuybackAttributes(form) };
            break;
        case CampaignType.DEAL:
            data.guardrails = { deal: buildDealGuardrails(form) };
            break;
        case CampaignType.INSTANT_BANK_DISCOUNT:
            data.guardrails = { instantBankDiscount: buildInstantBankDiscountGuardrails(form) };
            data.attributes = { instantBankDiscount: buildInstantBankDiscountAttributes(form) };
            break;
    }

    return data;
}

// ============================================================================
// Date/Time Helpers
// ============================================================================

/**
 * Converts a date string (YYYY-MM-DD) and time string (HH:mm) to epoch
 * milliseconds, interpreting the values in the marketplace timezone.
 *
 * Delegates to `localPartsToEpoch` from commonUtils which uses
 * Intl.DateTimeFormat for timezone-aware conversion. This makes the
 * conversion configurable for future marketplace expansion beyond India.
 *
 * @param dateStr - Date in YYYY-MM-DD format
 * @param timeStr - Time in HH:mm format (defaults to "00:00" if empty)
 * @returns Epoch milliseconds, or undefined if dateStr is empty
 */
export function toEpochMillis(dateStr: string, timeStr: string): number | undefined {
    return localPartsToEpoch(dateStr, timeStr);
}

// ============================================================================
// ASIN Parsing
// ============================================================================

/**
 * Parses a raw ASIN string (comma/newline/CR delimited) into a clean array.
 *
 * Duplicates are removed on the UI side (via a Set) so the API payload only
 * ever contains unique ASINs.
 *
 * @param asinsText - Raw ASIN text from the textarea
 * @returns Array of trimmed, non-empty, de-duplicated ASIN strings
 */
export function parseAsins(asinsText: string): string[] {
    if (!asinsText) return [];
    const asins = asinsText
        .split(/[,\n\r]+/)
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
    return Array.from(new Set(asins));
}
